import React, { useMemo } from 'react';
import { motion, useReducedMotion } from 'framer-motion';
import { AbaskHeroSection } from '@/components/hero/AbaskHeroSection';
import type { HomeSection } from '@/hooks/useHomeSections';
import { cn } from '@/lib/utils';

// Scaffold only: renders current hero content without visual changes.
// We will enhance this with a cinematic timeline behind a feature flag.

export interface CinematicIntroProps {
  section: HomeSection;
  className?: string;
}

export const CinematicIntro: React.FC<CinematicIntroProps> = ({ section, className }) => {
  // Respect user/system preferences and lower-end devices
  const prefersReduced = useReducedMotion();
  const lowMemory = useMemo(() => {
    try {
      const dm = (navigator as Navigator & { deviceMemory?: number }).deviceMemory;
      return typeof dm === 'number' && dm <= 4; // treat <=4GB as low-end
    } catch (_) {
      return false;
    }
  }, []);

  const viewportH = useMemo(() => (typeof window !== 'undefined' ? window.innerHeight : 800), []);

  const enableMotion = !prefersReduced && !lowMemory;

  return (
    <div className={cn('relative h-full', className)}>
      {/* Decorative layers behind content (keep subtle for readability) */}
      <div className="pointer-events-none absolute inset-0 z-0" aria-hidden>
        {/* Soft vignette around edges */}
        <div
          className="absolute inset-0"
          style={{
            // Radial vignette that gently darkens edges; keeps center clear
            background:
              'radial-gradient(120% 90% at 50% 40%, rgba(0,0,0,0) 60%, rgba(0,0,0,0.25) 100%)',
          }}
        />

        {/* Subtle top/bottom cinematic gradient to frame content */}
        <div className="absolute inset-x-0 top-0 h-24 bg-gradient-to-b from-black/30 via-black/10 to-transparent" />
        <div className="absolute inset-x-0 bottom-0 h-32 bg-gradient-to-t from-black/40 via-black/10 to-transparent" />

        {/* Diagonal shimmer line (gold) with gentle drift */}
        <motion.div
          className="absolute -left-[10%] top-1/3 w-[120%] h-px -rotate-6"
          style={{
            background:
              'linear-gradient(90deg, transparent 0%, rgba(198,161,91,0.0) 10%, rgba(198,161,91,0.35) 50%, rgba(198,161,91,0.0) 90%, transparent 100%)',
            filter: 'drop-shadow(0 0 8px rgba(198,161,91,0.25))',
          }}
          initial={enableMotion ? { opacity: 0 } : { opacity: 0.25 }}
          animate={enableMotion ? { opacity: [0.0, 0.35, 0.2, 0.3] } : undefined}
          transition={enableMotion ? { duration: 6, repeat: Infinity, ease: 'easeInOut' } : undefined}
        />

        {/* Floating micro-particles rising gently (disabled on low perf) */}
        {enableMotion && (
          <div className="absolute inset-0 overflow-hidden">
            {[...Array(8)].map((_, i) => {
              const delay = (i % 4) * 0.8 + Math.random() * 0.6;
              const duration = 8 + (i % 3) * 2 + Math.random() * 2;
              const startX = Math.random() * 100; // vw
              const size = 1 + (i % 3);
              const opacity = 0.08 + (i % 3) * 0.04;
              return (
                <motion.span
                  key={i}
                  className="absolute rounded-full bg-white"
                  style={{
                    left: `${startX}vw`,
                    bottom: '-20px',
                    width: `${size}px`,
                    height: `${size}px`,
                    opacity,
                    filter: 'blur(0.3px)',
                  }}
                  initial={{ y: 0, opacity: 0 }}
                  animate={{ y: -viewportH - 40, opacity: [0, opacity, 0] }}
                  transition={{ duration, delay, repeat: Infinity, ease: 'linear' }}
                />
              );
            })}
          </div>
        )}
      </div>

      {/* Content above our background FX */}
      <div className="relative z-10 h-full">
        <AbaskHeroSection section={section} className="h-full" />
      </div>
    </div>
  );
};

export default CinematicIntro;
